import React, { useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { MapPin, Award, Heart, Clock } from 'lucide-react';
import {
  motion,
  useInView,
  AnimatedCounter,
  FadeInWhenVisible,
  ScaleOnHover
} from './animations';

const reasons = [
  {
    icon: MapPin,
    title: '3 sedes en Valencia',
    description: 'Ubicaciones estrategicas para tu comodidad en toda la ciudad'
  },
  {
    icon: Heart,
    title: 'Valoracion gratuita',
    description: 'Consulta personalizada sin compromiso para disenar tu tratamiento ideal'
  },
  {
    icon: Award,
    title: 'Especialistas certificadas',
    description: 'Equipo profesional con amplia experiencia en medicina y estetica'
  },
  {
    icon: Clock,
    title: 'Resultados naturales',
    description: 'Enfoque en realzar tu belleza natural con resultados armonicos'
  }
];

const stats = [
  { value: 10, suffix: '+', label: 'Anos de experiencia' },
  { value: 5000, suffix: '+', label: 'Clientas satisfechas' },
  { value: 3, suffix: '', label: 'Sedes en Valencia' },
  { value: 100, suffix: '%', label: 'Profesionales certificados' }
];

const ReasonCard = ({ reason, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-50px" });
  const Icon = reason.icon;

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{
        duration: 0.5,
        delay: index * 0.12,
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        <Card className="group hover:shadow-elegant transition-all duration-300 border-border/50 bg-card text-center h-full">
          <CardContent className="p-6 flex flex-col items-center h-full">
            {/* Animated Icon Container */}
            <motion.div
              className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-4"
              initial={{ scale: 0, rotate: -180 }}
              animate={isInView ? { scale: 1, rotate: 0 } : {}}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2 + index * 0.1
              }}
              whileHover={{
                scale: 1.15,
                rotate: 10,
                boxShadow: "0 15px 30px -10px rgba(244, 143, 177, 0.4)"
              }}
            >
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <Icon className="w-8 h-8 text-primary-foreground" />
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h3
              className="text-lg font-bold text-foreground mb-3"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.02, color: "hsl(var(--primary))" }}
            >
              {reason.title}
            </motion.h3>

            {/* Description */}
            <motion.p
              className="text-sm text-muted-foreground leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              {reason.description}
            </motion.p>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

const StatItem = ({ stat, index }) => {
  const statRef = useRef(null);
  const isInView = useInView(statRef, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={statRef}
      className="text-center"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: index * 0.15
      }}
    >
      <motion.div
        className="text-3xl sm:text-4xl font-bold text-gradient-primary mb-2"
        whileHover={{ scale: 1.1 }}
        transition={{ type: "spring", stiffness: 400 }}
      >
        {isInView && (
          <AnimatedCounter
            value={stat.value}
            suffix={stat.suffix}
            duration={2}
          />
        )}
      </motion.div>
      <motion.div
        className="text-sm text-muted-foreground"
        initial={{ opacity: 0, y: 10 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.5 + index * 0.1 }}
      >
        {stat.label}
      </motion.div>
    </motion.div>
  );
};

const WhyChooseUs = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      ref={sectionRef}
      className="py-20 px-4 sm:px-6 lg:px-8 bg-primary-muted/30 overflow-hidden"
    >
      {/* Decorative background elements */}
      <motion.div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
      >
        <motion.div
          className="absolute top-20 -left-20 w-40 h-40 bg-primary/5 rounded-full blur-3xl"
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-20 w-60 h-60 bg-secondary/5 rounded-full blur-3xl"
          animate={{
            x: [0, -30, 0],
            y: [0, 20, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="container mx-auto relative">
        {/* Section Header */}
        <FadeInWhenVisible direction="up" className="text-center mb-16">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Por que{' '}
            <motion.span
              className="text-gradient-primary inline-block"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              elegirnos?
            </motion.span>
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Confianza, experiencia y resultados que hablan por si mismos
          </motion.p>
        </FadeInWhenVisible>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {reasons.map((reason, index) => (
            <ReasonCard key={index} reason={reason} index={index} />
          ))}
        </div>

        {/* Stats Section with animated counters */}
        <motion.div
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {stats.map((stat, index) => (
            <StatItem key={index} stat={stat} index={index} />
          ))}
        </motion.div>

        {/* Decorative line */}
        <motion.div
          className="mt-16 flex justify-center"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="w-24 h-1 bg-gradient-primary rounded-full" />
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
